import { useState, useEffect, useMemo, useCallback } from 'react'
import { Plus, Square, ChevronDown, ChevronRight } from 'lucide-react'
import type { ContainerInfo } from '../store/activitySlice'
import { fetchContainerLogs } from '../store/activitySlice'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import './ContainerList.css'

const ACTIVE_STATUSES = ['pending', 'starting', 'started', 'idle', 'processing']

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function statusBadgeClass(status: string): string {
  if (ACTIVE_STATUSES.includes(status)) return 'container-list__status-badge--active'
  if (status === 'failed') return 'container-list__status-badge--failed'
  if (status === 'pending') return 'container-list__status-badge--pending'
  return 'container-list__status-badge--stopped'
}

function formatLogTime(ts: string): string {
  const date = new Date(ts)
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function ContainerItem({ container: c, appId, isSignedIn, onStop }: { container: ContainerInfo; appId: string; isSignedIn: boolean; onStop: (containerId: string) => void }) {
  const dispatch = useAppDispatch()
  const logs = useAppSelector((state) => state.activity.containerLogs[c.container_id])
  const logsLoading = useAppSelector((state) => state.activity.containerLogsLoading[c.container_id])
  const [expanded, setExpanded] = useState(false)

  const isActive = ACTIVE_STATUSES.includes(c.status)
  const hasError = c.status === 'failed' && c.error_message
  const time = c.last_event_at || c.created_at

  const handleToggle = useCallback(() => {
    if (!expanded && !logsLoading) {
      dispatch(fetchContainerLogs({ appId, containerId: c.container_id }))
    }
    setExpanded(!expanded)
  }, [expanded, logsLoading, dispatch, appId, c.container_id])

  return (
    <div className={`container-list__item-wrap${hasError ? ' container-list__item-wrap--failed' : ''}`} data-testid="container-list-item">
      <div className="container-list__item-row">
        <button className="container-list__item-btn" onClick={handleToggle} aria-expanded={expanded}>
          {expanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
          <span className={`container-list__status-badge ${statusBadgeClass(c.status)}`}>{c.status}</span>
          <span className="container-list__name">{c.container_id}</span>
          <span className="container-list__time">{formatRelativeTime(time)}</span>
        </button>
        {isActive && isSignedIn && (
          <button
            className="container-list__stop-btn"
            onClick={() => onStop(c.container_id)}
            aria-label={`Stop container ${c.container_id}`}
            data-testid="stop-container-btn"
          >
            <Square size={12} aria-hidden="true" />
            Stop
          </button>
        )}
      </div>
      {hasError && (
        <div className="container-list__error" title={c.error_message!}>{c.error_message}</div>
      )}
      {expanded && (
        logsLoading ? (
          <div className="container-list__logs-loading">Loading logs...</div>
        ) : logs && logs.length > 0 ? (
          <div className="container-list__logs" data-testid="container-list-logs">
            {logs.map((log) => (
              <div key={log.id} className="container-list__log-entry" data-testid="container-list-log-entry">
                <span className="container-list__log-time">{formatLogTime(log.timestamp)}</span>
                <span className="container-list__log-type">[{log.log_type}]</span>
                <span className="container-list__log-message">{log.message}</span>
              </div>
            ))}
          </div>
        ) : logs ? (
          <div className="container-list__logs-empty">No activity logs for this container.</div>
        ) : null
      )}
    </div>
  )
}

function StoppedContainersGroup({ containers, appId, isSignedIn, onStop }: { containers: ContainerInfo[]; appId: string; isSignedIn: boolean; onStop: (containerId: string) => void }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="container-list__stopped-group" data-testid="stopped-containers-group">
      <button
        className="container-list__stopped-toggle"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        data-testid="stopped-containers-toggle"
      >
        {expanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
        <span>Stopped Containers</span>
        <span className="container-list__stopped-count">{containers.length}</span>
      </button>
      {expanded && (
        <div className="container-list__stopped-items" data-testid="stopped-containers-items">
          {containers.map((c) => (
            <ContainerItem key={c.container_id} container={c} appId={appId} isSignedIn={isSignedIn} onStop={onStop} />
          ))}
        </div>
      )}
    </div>
  )
}

interface ContainerListProps {
  containers: ContainerInfo[]
  appId: string
  onRefresh: () => void
}

function ContainerList({ containers, appId, onRefresh }: ContainerListProps) {
  const [spawning, setSpawning] = useState(false)
  const [spawnError, setSpawnError] = useState<string | null>(null)
  const isSignedIn = useAppSelector((state) => !!state.auth.user)

  const handleStop = useCallback(async (containerId: string) => {
    try {
      const resp = await fetch('/api/stop-container', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ containerId }),
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        setSpawnError(data.error || `Stop failed (${resp.status})`)
      } else {
        onRefresh()
      }
    } catch (err) {
      setSpawnError(err instanceof Error ? err.message : 'Network error')
    }
  }, [onRefresh])

  const { active, defunct } = useMemo(() => {
    const active: ContainerInfo[] = []
    const defunct: ContainerInfo[] = []
    for (const c of containers) {
      if (ACTIVE_STATUSES.includes(c.status)) {
        active.push(c)
      } else {
        defunct.push(c)
      }
    }
    return { active, defunct }
  }, [containers])

  const handleSpawn = useCallback(async () => {
    setSpawning(true)
    setSpawnError(null)
    try {
      const resp = await fetch('/api/spawn-app-container', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId }),
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        setSpawnError(data.error || `Failed (${resp.status})`)
      } else {
        onRefresh()
      }
    } catch (err) {
      setSpawnError(err instanceof Error ? err.message : 'Network error')
    } finally {
      setSpawning(false)
    }
  }, [appId, onRefresh])

  const renderItem = (c: ContainerInfo) => {
    return <ContainerItem key={c.container_id} container={c} appId={appId} isSignedIn={isSignedIn} onStop={handleStop} />
  }

  return (
    <section className="container-list" data-testid="container-list" aria-labelledby="container-list-heading">
      <div className="container-list__header">
        <h2 className="container-list__title" id="container-list-heading">Containers</h2>
        <span className="container-list__count" aria-label={`${containers.length} containers`}>{containers.length}</span>
        {isSignedIn && (
          <button
            className="container-list__spawn-btn"
            onClick={handleSpawn}
            disabled={spawning}
            data-testid="spawn-container-btn"
          >
            <Plus size={14} aria-hidden="true" />
            {spawning ? 'Spawning...' : 'Spawn Container'}
          </button>
        )}
      </div>
      {spawnError && (
        <div className="container-list__error" style={{ padding: '8px 20px' }}>{spawnError}</div>
      )}
      {containers.length === 0 ? (
        <div className="container-list__empty">No containers</div>
      ) : (
        <div className="container-list__items">
          {active.length > 0 && active.map(renderItem)}
          {defunct.length > 0 && (
            <StoppedContainersGroup containers={defunct} appId={appId} isSignedIn={isSignedIn} onStop={handleStop} />
          )}
        </div>
      )}
      {isSignedIn && <ContainerLimitFooter appId={appId} />}
    </section>
  )
}

function ContainerLimitFooter({ appId }: { appId: string }) {
  const appLimit = useAppSelector((state) => state.apps.items.find((a) => a.id === appId)?.container_limit)
  const [limit, setLimit] = useState<number | null>(null)
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setLimit(appLimit ?? 1)
  }, [appLimit])

  const handleSave = useCallback(async () => {
    const val = parseInt(editValue, 10)
    if (isNaN(val) || val < 0) return
    setSaving(true)
    try {
      const resp = await fetch(`/api/apps/${appId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ containerLimit: val }),
      })
      if (resp.ok) {
        setLimit(val)
        setEditing(false)
      }
    } catch { /* ignore */ }
    setSaving(false)
  }, [editValue, appId])

  if (limit === null) return null

  return (
    <div className="container-list__footer">
      <span className="container-list__footer-label">Container limit:</span>
      {editing ? (
        <span className="container-list__footer-edit">
          <input
            type="number"
            min={0}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="container-list__footer-input"
            autoFocus
          />
          <button className="container-list__footer-save" onClick={handleSave} disabled={saving}>
            {saving ? '...' : 'Save'}
          </button>
          <button className="container-list__footer-cancel" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </span>
      ) : (
        <span className="container-list__footer-value">
          {limit}
          <button className="container-list__footer-edit-btn" onClick={() => { setEditValue(String(limit)); setEditing(true) }}>
            Edit
          </button>
        </span>
      )}
    </div>
  )
}

export default ContainerList
